import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiFileText, FiAlertCircle, FiTrash2, FiThumbsUp, FiThumbsDown,
  FiSearch,
} from 'react-icons/fi';
import CommissionScanClientFiles, { AmsFileSelection } from '../commissionscan/CommissionScanClientFiles';
import { useToast } from '../../context/ToastContext';
import '../commissionscan/CommissionScan.css';
import '../../styles/Voice.css';

const getToken    = () => localStorage.getItem('toolboxToken') || '';
const getNcToken  = () => localStorage.getItem('amsToken')     || '';

type UploadTab = 'local' | 'client';
type DataTab   = 'insured' | 'coverage' | 'drivers' | 'vehicles';

interface Props { onGoToHistory?: () => void; }

const MTMScanUpload: React.FC<Props> = ({ onGoToHistory }) => {

  /* ── Upload phase ── */
  const [uploadTab,       setUploadTab]       = useState<UploadTab>('local');
  const [documentType,    setDocumentType]    = useState<'Binder' | 'Quote'>('Binder');
  const [file,            setFile]            = useState<File | null>(null);
  const [selectedAmsFile, setSelectedAmsFile] = useState<AmsFileSelection | null>(null);
  const [isDragging,      setIsDragging]      = useState(false);
  const [uploadLoading,   setUploadLoading]   = useState(false);
  const [uploadError,     setUploadError]     = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ── Scan results ── */
  const [phase,           setPhase]           = useState<'options' | 'upload' | 'review'>('options');
  const [submissionId,    setSubmissionId]    = useState('');
  const [fileName,        setFileName]        = useState('');
  const [pdfUrl,          setPdfUrl]          = useState('');
  const [dataTab,         setDataTab]         = useState<DataTab>('insured');

  /* ── Insured fields (editable) ── */
  const [insuredName,     setInsuredName]     = useState('');
  const [address,         setAddress]         = useState('');
  const [city,            setCity]            = useState('');
  const [stateName,       setStateName]       = useState('');
  const [zipCode,         setZipCode]         = useState('');
  const [policyNumber,    setPolicyNumber]    = useState('');
  const [policyPremium,   setPolicyPremium]   = useState('');
  const [effectiveDate,   setEffectiveDate]   = useState('');
  const [expirationDate,  setExpirationDate]  = useState('');

  /* ── Read-only data ── */
  const [coverage,  setCoverage]  = useState<any>(null);
  const [drivers,   setDrivers]   = useState<any[]>([]);
  const [vehicles,  setVehicles]  = useState<any[]>([]);

  /* ── Submit to AMS ── */
  const [createNewClient,    setCreateNewClient]    = useState(true);
  const [clientSearch,       setClientSearch]       = useState('');
  const [clientList,         setClientList]         = useState<any[]>([]);
  const [clientLoading,      setClientLoading]      = useState(false);
  const [selectedClientId,   setSelectedClientId]   = useState('');
  const [selectedClientName, setSelectedClientName] = useState('');
  const [clientError,        setClientError]        = useState('');
  const [submitLoading,      setSubmitLoading]      = useState(false);
  const [submitSuccess,      setSubmitSuccess]      = useState(false);
  const [submitError,        setSubmitError]        = useState('');
  const [ncUrl,              setNcUrl]              = useState('');

  /* ── Feedback ── */
  const [feedbackText,       setFeedbackText]       = useState('');
  const [feedbackRating,     setFeedbackRating]     = useState<boolean | null>(null);
  const [feedbackAnchor,     setFeedbackAnchor]     = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSuccess,    setFeedbackSuccess]    = useState(false);
  const [feedBackError,      setFeedBackError]      = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  /* ── Toast ── */
  const { showSuccess } = useToast();

  /* ── Client search debounce (existing client) ── */
  useEffect(() => {
    if (!clientSearch.trim() || createNewClient) { setClientList([]); return; }
    const t = setTimeout(() => {
      setClientLoading(true);
      axios.get(`${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/customerlist`, {
        params: { searchText: clientSearch, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToken()}` },
      })
        .then(r => setClientList(r.data || []))
        .finally(() => setClientLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [clientSearch, createNewClient]);

  /* ── Feedback helpers ── */
  const closeFeedback = useCallback(() => {
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
    setFeedbackSuccess(false);
  }, []);

  const openFeedbackPopup = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
    setFeedbackSuccess(false);
    const right = window.innerWidth - rect.right;
    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow < 320) {
      setFeedbackAnchor({ bottom: window.innerHeight - rect.top + 8, right });
    } else {
      setFeedbackAnchor({ top: rect.bottom + 8, right });
    }
  }, []);

  const onThumbsUpClick = useCallback(() => {
    const next = feedbackRating === true ? null : true;
    closeFeedback();
    axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/MTMScan/isliked`,
      { TransactionId: submissionId, IsLiked: next, Feedback: feedbackText ?? '' },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).then(() => { showSuccess('Feedback submitted successfully!'); }).catch(() => {});
  }, [feedbackRating, submissionId, feedbackText, closeFeedback, showSuccess]);

  const onThumbsDownClick = useCallback(() => {
    const next = feedbackRating === false ? null : false;
    closeFeedback();
    axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/MTMScan/isliked`,
      { TransactionId: submissionId, IsLiked: next, Feedback: feedbackText ?? '' },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).then(() => { showSuccess('Feedback submitted successfully!'); }).catch(() => {});
  }, [feedbackRating, submissionId, feedbackText, closeFeedback, showSuccess]);

  const onSubmitFeedbackClick = useCallback(() => {
    if (!feedbackText.trim()) { setFeedBackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedBackError('Please keep feedback under 250 characters'); return; }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/MTMScan/feedback`,
      { TransactionId: submissionId, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch(() => { setFeedbackSubmitting(false); });
  }, [submissionId, feedbackText, closeFeedback, showSuccess]);

  /* ── File helpers ── */
  const pickFile = useCallback((incoming: FileList | File[]) => {
    const first = Array.from(incoming).find(
      f => f.type === 'application/pdf' || /\.pdf$/i.test(f.name),
    );
    if (first) { setFile(first); setUploadError(''); }
    else { setUploadError('Please select a PDF file.'); setTimeout(() => setUploadError(''), 3000); }
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    pickFile(e.dataTransfer.files);
  };

  const canScan = !uploadLoading && (uploadTab === 'local' ? !!file : !!selectedAmsFile);

  /* ── Start Scan ── */
  const onStartScan = useCallback(async () => {
    const isAms = uploadTab === 'client';
    if (!isAms && !file) return;
    if (isAms && !selectedAmsFile) return;

    setUploadLoading(true);
    setUploadError('');

    try {
      const insuredId = isAms ? (selectedAmsFile?.insuredId || '') : '';
      const folderId  = isAms ? (selectedAmsFile?.folderId  || '') : '';
      const fileIds   = isAms ? [selectedAmsFile?.file.id   || ''] : [];

      const formData = new FormData();
      if (!isAms && file) formData.append('file', file);
      if (isAms) fileIds.forEach(id => formData.append('amsFileIds', id));
      formData.append('documentType',         documentType);
      formData.append('ncToken',              getNcToken());
      formData.append('insuredId',            insuredId);
      formData.append('folderId',             folderId);
      formData.append('isFileUploadFromAMS',  String(isAms));
      formData.append('selectedOption',       JSON.stringify({ insuredId, folderId, fileIds, isFileUploadFromAMS: isAms }));

      const res = await axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/MTMScan/mtmscan`, formData, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });

      const subId = res.data?.submissionId || '';
      const fn    = isAms ? (selectedAmsFile?.file.name || '') : (file?.name || '');
      const data  = res.data?.responseJson?.[0] || {};

      setSubmissionId(subId);
      setFileName(fn);
      setPdfUrl(res.data?.fileUrl || '');

      setInsuredName(data.namedInsured    || '');
      setAddress(data.streetAddress       || '');
      setCity(data.city                   || '');
      setStateName(data.state             || '');
      setZipCode(data.zipCode             || '');
      setPolicyNumber(data.policyNumber   || '');
      setPolicyPremium(data.policyPremium || '');
      const toInputDate = (d: string): string => {
        if (!d) return '';
        try {
          const parsed = new Date(d);
          if (isNaN(parsed.getTime())) return '';
          const y = parsed.getFullYear();
          const m = String(parsed.getMonth() + 1).padStart(2, '0');
          const day = String(parsed.getDate()).padStart(2, '0');
          return `${y}-${m}-${day}`;
        } catch { return ''; }
      };
      setEffectiveDate(toInputDate(data.effectiveDate   || ''));
      setExpirationDate(toInputDate(data.expirationDate || ''));

      setCoverage(data.autoMobileLiability || null);
      setDrivers(data.driverSchedule       || []);
      setVehicles(data.vehicles            || []);

      setPhase('review');
      setDataTab('insured');
    } catch (err: any) {
      if (err.response?.status === 402) {
        setUploadError(err.response.data || 'Insufficient balance.');
      } else {
        setUploadError(err.response?.data?.errorMessage || 'Error while scanning the document.');
        setTimeout(() => setUploadError(''), 4000);
      }
    } finally {
      setUploadLoading(false);
    }
  }, [file, selectedAmsFile, uploadTab, documentType]);

  /* ── Submit to AMS ── */
  const onSubmitToAMS = useCallback(async () => {
    if (!createNewClient && !selectedClientId) {
      setSubmitError('Please select an existing client.');
      return;
    }
    setSubmitLoading(true);
    setSubmitError('');

    try {
      let insuredDatabaseId = selectedClientId;

      if (createNewClient) {
        const insRes = await axios.post(
          `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/insertinsured?nowCertsToken=${getNcToken()}`,
          {
            commercialName: insuredName, firstName: '', lastName: '', dba: '',
            addressLine1: address, addressLine2: '', state: stateName, city,
            zipCode, eMail: '', cellPhone: '', fein: '', insuredType: 0,
          },
          { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } },
        );
        insuredDatabaseId = insRes.data?.database_id || '';
        setNcUrl(insRes.data?.newInsuredLunchUrl || '');
        if (!insuredDatabaseId) throw new Error('Failed to create insured in AMS.');
      }

      const fmtDate = (d: string) => {
        try { return d ? new Date(d).toLocaleDateString('en-US') : ''; } catch { return d; }
      };
      await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/addpolicy?nowCertsToken=${getNcToken()}`,
        {
          insuredDatabaseId,
          firstName:             '',
          lastName:              '',
          insuredCommercialName: insuredName,
          number:                policyNumber,
          effectiveDate:         fmtDate(effectiveDate),
          expirationDate:        fmtDate(expirationDate),
          bindDate:              '',
          businessType:          0,
          billingType:           0,
          mgaName:               '',
          carrierName:           '',
          premium:               parseFloat(policyPremium.replace(/[,$]/g, '')) || 0,
          lineOfBusinessName:    '',
          agencyFee:             0,
          taxes:                 0,
          isPolicyRenewal:       false,
          parentPolicyId:        '',
        },
        { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } },
      );

      let vehicleDbIds: string[] = [];
      if (vehicles.length > 0) {
        const vRes = await axios.post(
          `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/addvehicles?ncToken=${getNcToken()}`,
          vehicles.map(v => ({
            insured_database_id:      insuredDatabaseId,
            vinNumber:                v.vin  || '',
            vehicleManufacturer:      v.make || '',
            vehicleType:              'Truck Tractor',
            year:                     Number(v.year) || 0,
            insured_commercial_name:  insuredName,
            check: true,
          })),
          { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } },
        );
        vehicleDbIds = (vRes.data || [])
          .map((v: any) => v.vehicleDatabaseId || v.database_id || '')
          .filter(Boolean);
      }

      if (vehicleDbIds.length > 0 && coverage) {
        await axios.post(
          `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/addvehiclesCoverage?ncToken=${getNcToken()}`,
          vehicleDbIds.map(vId => ({
            vehicleDatabaseId:        vId,
            insureddatabaseid:        insuredDatabaseId,
            bodilyinjurylimit:        coverage.liabilityLimit || coverage.bodilyInjuryPropertyDamageLimit || '',
            bodilyinjurypremium:      '0',
            propertydamagelimit:      coverage.liabilityLimit || '',
            propertydamagepremium:    '0',
            umbilimit:                coverage.umbiLimit    || '',
            umbipremium:              '0',
            uimbilimit:               coverage.uimbiLimit   || '',
            uimbipremium:             '0',
            umpdlimit:                coverage.umpdLimit    || '',
            umpdpremium:              '0',
            medicalpaymentslimit:     coverage.medicalPaymentsLimit || '',
            medicalpaymentspremium:   '0',
            piplimit:                 coverage.personalInjuryProtectionLimit || '',
            pippremium:               '0',
          })),
          { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } },
        );
      }

      if (drivers.length > 0) {
        const splitName = (name: string) => {
          const parts = (name || '').trim().split(' ');
          return { first: parts[0] || '', last: parts.slice(1).join(' ') || '' };
        };
        await axios.post(
          `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/adddrivers?ncToken=${getNcToken()}`,
          drivers.map(d => {
            const { first, last } = splitName(d.name);
            return {
              insured_database_id:      insuredDatabaseId,
              insured_commercial_name:  insuredName,
              first_name:               first,
              last_name:                last,
              license_number:           d.licenseNumber || '',
              date_of_birth:            d.dob           || '',
              excluded:                 d.excluded === 'Y',
              active: true,
            };
          }),
          { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } },
        );
      }

      await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/NowCert/updatemtmScan?submissionId=${submissionId}`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );

      await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/uploadV1`,
        JSON.stringify({
          TransactionId: submissionId,
          FileName:      fileName,
          NCPolicyId:    '',
          NCInsuredId:   insuredDatabaseId,
          FileData:      '',
          FolderName:    'MTMScan',
          TargetName:    'MTMScan',
          DocumentType:  documentType,
          NowCertsToken: getNcToken(),
        }),
        { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } },
      );

      setSubmitSuccess(true);
      showSuccess('Successfully data added in AMS');
    } catch (err: any) {
      setSubmitError(err.response?.data?.errorMessage || err.message || 'Error submitting to AMS.');
    } finally {
      setSubmitLoading(false);
    }
  }, [
    createNewClient, selectedClientId, insuredName, address, city, stateName,
    zipCode, policyNumber, policyPremium, effectiveDate, expirationDate,
    vehicles, drivers, coverage, submissionId, fileName, documentType,
  ]);

  const onGoToUpload = useCallback(() => {
    if (!createNewClient && !selectedClientId) {
      setClientError('Please select a client.');
      return;
    }
    setClientError('');
    setPhase('upload');
  }, [createNewClient, selectedClientId]);

  const onBackToOptions = useCallback(() => {
    setPhase('options');
  }, []);

  const handleReset = useCallback(() => {
    setPhase('options');
    setUploadTab('local');
    setDocumentType('Binder');
    setFile(null);
    setSelectedAmsFile(null);
    setIsDragging(false);
    setUploadError('');
    setSubmissionId('');
    setFileName('');
    setPdfUrl('');
    setInsuredName(''); setAddress(''); setCity(''); setStateName(''); setZipCode('');
    setPolicyNumber(''); setPolicyPremium(''); setEffectiveDate(''); setExpirationDate('');
    setCoverage(null); setDrivers([]); setVehicles([]);
    setCreateNewClient(true);
    setClientSearch(''); setClientList([]); setSelectedClientId(''); setSelectedClientName('');
    setSubmitLoading(false); setSubmitSuccess(false); setSubmitError(''); setNcUrl('');
    closeFeedback();
  }, [closeFeedback]);

  /* ─────────────────────────────────────────────────────────────
     OPTIONS PHASE — Client & Policy selection
  ───────────────────────────────────────────────────────────── */
  if (phase === 'options') {
    return (
      <div className="cs-main" style={{ padding: 24, gap: 20 }}>
        {/* Client */}
        <div>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Client
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {([
              [true,  'Create New'],
              [false, 'Use Existing'],
            ] as [boolean, string][]).map(([val, label]) => (
              <div
                key={label}
                onClick={() => {
                  setCreateNewClient(val);
                  setClientError('');
                  if (val) { setClientSearch(''); setSelectedClientId(''); setSelectedClientName(''); }
                }}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: 'var(--surface-glass)',
                  border: `1px solid ${createNewClient === val ? 'var(--accent-purple, #8b5cf6)' : 'var(--border-glass)'}`,
                  borderRadius: 8, padding: '10px 14px', cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>{label}</span>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${createNewClient === val ? 'var(--accent-purple, #8b5cf6)' : 'var(--border-glass)'}`,
                  background: createNewClient === val ? 'var(--accent-purple, #8b5cf6)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {createNewClient === val && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
                </div>
              </div>
            ))}

            {/* Existing client search */}
            {!createNewClient && (
              <div style={{ position: 'relative' }}>
                <div className="cs-client-search__input-wrap">
                  <FiSearch size={13} className="cs-client-search__icon" />
                  <input
                    className="cs-client-search__input"
                    placeholder={selectedClientName || 'Search client name…'}
                    value={clientSearch}
                    autoFocus
                    onChange={e => {
                      setClientSearch(e.target.value);
                      setClientError('');
                      if (!e.target.value) { setSelectedClientId(''); setSelectedClientName(''); }
                    }}
                  />
                  {clientLoading && <span className="cs-client-search__spinner" />}
                </div>
                {clientSearch.trim() && clientList.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4,
                    background: 'var(--bg-primary)', border: '1px solid var(--border-glass)',
                    borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', maxHeight: 180, overflowY: 'auto',
                  }}>
                    {clientList.map((c: any) => (
                      <div
                        key={c.databaseId}
                        style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-glass)' }}
                        onClick={() => {
                          setSelectedClientId(c.databaseId);
                          setSelectedClientName(c.name);
                          setClientSearch('');
                          setClientList([]);
                          setClientError('');
                        }}
                      >
                        {c.name}
                      </div>
                    ))}
                  </div>
                )}
                {selectedClientName && (
                  <span style={{ fontSize: 11, color: '#22c55e', marginTop: 4, display: 'block' }}>
                    Selected: {selectedClientName}
                  </span>
                )}
              </div>
            )}
            {clientError && (
              <p style={{ color: '#ef4444', fontSize: 12, margin: 0 }}>{clientError}</p>
            )}
          </div>
        </div>

        {/* Policy */}
        <div>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Policy
          </p>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: 'var(--surface-glass)', border: '1px solid var(--accent-purple, #8b5cf6)',
            borderRadius: 8, padding: '10px 14px',
          }}>
            <span style={{ fontSize: 13, color: 'var(--text-primary)' }}>Create New</span>
            <div style={{
              width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
              border: '2px solid var(--accent-purple, #8b5cf6)', background: 'var(--accent-purple, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />
            </div>
          </div>
        </div>

        {/* Next */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <motion.button
            className="cs-btn-start"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={onGoToUpload}
          >
            Next →
          </motion.button>
        </div>
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────
     REVIEW PHASE — 4 data tabs + AMS submit panel
  ───────────────────────────────────────────────────────────── */
  if (phase === 'review') {
    return (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

          {/* ── Split panel: PDF left + data right ── */}
          <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>

            {/* Left: PDF */}
            {pdfUrl && (
              <div style={{ width: '45%', flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border-glass)' }}>
                <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-glass)', flexShrink: 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Uploaded PDF</span>
                </div>
                <div style={{ flex: 1, minHeight: 0 }}>
                  <iframe src={pdfUrl} title="MTM PDF" style={{ width: '100%', height: '100%', border: 'none' }} />
                </div>
              </div>
            )}

            {/* Right: data tabs */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Client / Policy summary bar */}
          <div style={{ padding: '7px 16px', borderBottom: '1px solid var(--border-glass)', background: 'var(--surface-glass)', flexShrink: 0, fontSize: 12, color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--text-primary)' }}>Client:</strong>{' '}{createNewClient ? 'Create New' : selectedClientName}
            {' · '}
            <strong style={{ color: 'var(--text-primary)' }}>Policy:</strong>{' '}Create New
          </div>
          {/* Data tabs header */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border-glass)', flexShrink: 0, background: 'var(--surface-glass)' }}>
            {([
              ['insured',  'Insured Details'],
              ['coverage', 'Coverage Limits'],
              ['drivers',  'Driver Scheduled'],
              ['vehicles', 'Scheduled Vehicles'],
            ] as [DataTab, string][]).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setDataTab(key)}
                style={{
                  padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: dataTab === key ? 700 : 500,
                  color: dataTab === key ? 'var(--text-primary)' : 'var(--text-secondary)',
                  borderBottom: dataTab === key ? '2px solid var(--accent-purple, #8b5cf6)' : '2px solid transparent',
                  fontFamily: 'inherit',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div style={{ flex: 1, overflow: 'auto', padding: '14px 16px' }}>

            {/* ── Insured Details ── */}
            {dataTab === 'insured' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Insured Information */}
                <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-glass)', borderRadius: 10, padding: '14px 16px' }}>
                  <h4 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 12px' }}>Insured Information</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px 14px' }}>
                    {([
                      ['Insured Name', insuredName, setInsuredName, 'text',   'Insured name'],
                      ['Address',      address,      setAddress,      'text',   'Street address'],
                      ['City',         city,         setCity,         'text',   'City'],
                      ['State',        stateName,    setStateName,    'text',   'State'],
                      ['Zip Code',     zipCode,      (v: string) => setZipCode(v.replace(/\D/g, '').slice(0, 9)), 'text', 'Zip code'],
                    ] as [string, string, (v: string) => void, string, string][]).map(([label, val, setter, type, ph]) => (
                      <div key={label}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 5, letterSpacing: '0.01em' }}>{label}</div>
                        <input
                          type={type}
                          style={{
                            width: '100%', padding: '7px 10px', boxSizing: 'border-box',
                            background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                            borderRadius: 6, fontSize: 13, color: 'var(--text-primary)',
                            outline: 'none', fontFamily: 'inherit',
                          }}
                          value={val}
                          placeholder={ph}
                          onChange={e => setter(e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Policy Information */}
                <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-glass)', borderRadius: 10, padding: '14px 16px' }}>
                  <h4 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 12px' }}>Policy Information</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px 14px' }}>
                    {([
                      ['Policy Number',   policyNumber,   setPolicyNumber,   'text', 'Policy number'],
                      ['Policy Premium ($)', policyPremium, setPolicyPremium, 'text', 'Premium'],
                      ['Effective Date',  effectiveDate,  setEffectiveDate,  'date', ''],
                      ['Expiration Date', expirationDate, setExpirationDate, 'date', ''],
                    ] as [string, string, (v: string) => void, string, string][]).map(([label, val, setter, type, ph]) => (
                      <div key={label}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 5, letterSpacing: '0.01em' }}>{label}</div>
                        <input
                          type={type}
                          style={{
                            width: '100%', padding: '7px 10px', boxSizing: 'border-box',
                            background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                            borderRadius: 6, fontSize: 13, color: 'var(--text-primary)',
                            outline: 'none', fontFamily: 'inherit',
                          }}
                          value={val}
                          placeholder={ph || undefined}
                          onChange={e => setter(e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            )}

            {/* ── Coverage Limits ── */}
            {dataTab === 'coverage' && (
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border-glass)' }}>
                {!coverage ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>No coverage data.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--surface-glass)', borderBottom: '1px solid var(--border-glass)' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase' }}>Coverage</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase' }}>Limit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(documentType === 'Quote' ? [
                        ['Liability Limit',            coverage.liabilityLimit],
                        ['Deductible',                 coverage.deductible],
                        ['UM/BI Limit',                coverage.umbiLimit],
                        ['UIM/BI Limit',               coverage.uimbiLimit],
                        ['UM/PD Limit',                coverage.umpdLimit],
                        ['UIM/PD Limit',               coverage.uimpdLimit],
                        ['Medical Payments',           coverage.medicalPaymentsLimit],
                        ['PIP',                        coverage.personalInjuryProtectionLimit],
                        ['Auto Coverage Symbol',       coverage.autoCoverageSymbol],
                      ] : [
                        ['BI / PD Limit',              coverage.bodilyInjuryPropertyDamageLimit],
                        ['Liability Limit',            coverage.liabilityLimit],
                        ['Covered Autos — Liability',  coverage.coveredAutosSymbolLiability],
                        ['Covered Autos — Med Payments', coverage.coveredAutosSymbolAutoMedicalPayments],
                        ['Covered Autos — PIP',        coverage.coveredAutosSymbolPIP],
                        ['Covered Autos — UM/UIM',     coverage.coveredAutosSymbolUMandorUIM],
                        ['UM/BI Limit',                coverage.umbiLimit],
                        ['UIM/BI Limit',               coverage.uimbiLimit],
                        ['Medical Payments',           coverage.medicalPaymentsLimit],
                        ['PIP Limits',                 coverage.personalInjuryProtectionLimits],
                        ['UIIA',                       coverage.uiia],
                      ]).map(([label, val], i) => (
                        <tr key={String(label)} style={{ borderBottom: '1px solid var(--border-glass)', background: i % 2 !== 0 ? 'rgba(255,255,255,0.015)' : 'transparent' }}>
                          <td style={{ padding: '8px 12px', color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</td>
                          <td style={{ padding: '8px 12px', color: 'var(--text-primary)' }}>{val || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ── Driver Schedule ── */}
            {dataTab === 'drivers' && (
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border-glass)' }}>
                {drivers.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>No drivers.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--surface-glass)', borderBottom: '1px solid var(--border-glass)' }}>
                        {['License #', 'DOB', 'Name', 'Excluded'].map(col => (
                          <th key={col} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {drivers.map((d: any, i: number) => {
                        const licNo   = d.LicenseNumber || d.licenseNumber || '—';
                        const dob     = d.DOB  || d.Dob  || d.dob  || '—';
                        const drvName = d.Name || d.name || '—';
                        const excl    = d.Excluded || d.excluded || '';
                        const isExcluded = excl === 'Y' || excl === true;
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid var(--border-glass)', background: i % 2 !== 0 ? 'rgba(255,255,255,0.015)' : 'transparent' }}>
                            <td style={{ padding: '8px 12px', color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: 12 }}>{licNo}</td>
                            <td style={{ padding: '8px 12px', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{dob}</td>
                            <td style={{ padding: '8px 12px', color: 'var(--text-primary)' }}>{drvName}</td>
                            <td style={{ padding: '8px 12px' }}>
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
                                borderRadius: 12, fontSize: 11, fontWeight: 600,
                                color:      isExcluded ? '#ef4444' : '#22c55e',
                                background: isExcluded ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)',
                                border: `1px solid ${isExcluded ? 'rgba(239,68,68,0.25)' : 'rgba(34,197,94,0.25)'}`,
                              }}>
                                {isExcluded ? 'Yes' : 'No'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ── Scheduled Vehicles ── */}
            {dataTab === 'vehicles' && (
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid var(--border-glass)' }}>
                {vehicles.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>No vehicles.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--surface-glass)', borderBottom: '1px solid var(--border-glass)' }}>
                        {['VIN', 'Make', 'Year'].map(col => (
                          <th key={col} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase' }}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {vehicles.map((v: any, i: number) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--border-glass)', background: i % 2 !== 0 ? 'rgba(255,255,255,0.015)' : 'transparent' }}>
                          <td style={{ padding: '8px 12px', color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: 12 }}>{v.VIN || v.Vin || v.vin || '—'}</td>
                          <td style={{ padding: '8px 12px', color: 'var(--text-primary)' }}>{v.Make || v.make || '—'}</td>
                          <td style={{ padding: '8px 12px', color: 'var(--text-primary)' }}>{v.Year || v.year || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
            </div>
          </div>

          {/* ── AMS Submit Panel ── */}
          <div style={{
            flexShrink: 0, padding: '14px 20px', borderTop: '1px solid var(--border-glass)',
            background: 'var(--surface-glass)', display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
          }}>
            {submitSuccess ? (
              <>
                <span />
                <div style={{ display: 'flex', gap: 8 }}>
                  <motion.button
                    className="cs-btn-start"
                    onClick={handleReset}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Upload Again
                  </motion.button>
                  <button className="cs-btn-cancel" onClick={openFeedbackPopup}>
                    Feedback
                  </button>
                </div>
              </>
            ) : (
              <>
                {submitError && (
                  <div style={{ color: '#ef4444', fontSize: 12 }}>{submitError}</div>
                )}
                <span />
                <motion.button
                  className="cs-btn-start"
                  disabled={submitLoading || (!createNewClient && !selectedClientId)}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={onSubmitToAMS}
                >
                  {submitLoading ? <span className="cs-upload-spinner" /> : 'Submit'}
                </motion.button>
              </>
            )}
          </div>
        </div>

        {/* ── Feedback popup ── */}
        {feedbackAnchor && (
          <div
            ref={feedbackRef}
            className="csfb-popup"
            style={{
              top:    feedbackAnchor.top    !== undefined ? feedbackAnchor.top    : 'auto',
              bottom: feedbackAnchor.bottom !== undefined ? feedbackAnchor.bottom : 'auto',
              right:  feedbackAnchor.right,
            }}
          >
            <h4 className="csfb-title">Feedback</h4>
            <textarea
              className="csfb-textarea"
              placeholder="Enter your feedback here"
              value={feedbackText}
              onChange={e => setFeedbackText(e.target.value)}
              rows={4}
            />
            <div className="csfb-rating">
              <button
                className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
                onClick={onThumbsUpClick}
                title="Thumbs up"
              >
                <FiThumbsUp size={20} />
              </button>
              <button
                className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
                onClick={onThumbsDownClick}
                title="Thumbs down"
              >
                <FiThumbsDown size={20} />
              </button>
            </div>
            {feedBackError && <p className="csfb-error">{feedBackError}</p>}
            <div className="csfb-actions">
              <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
              <button
                className="csfb-btn-submit"
                disabled={feedbackSubmitting}
                onClick={onSubmitFeedbackClick}
              >
                {feedbackSubmitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  /* ─────────────────────────────────────────────────────────────
     UPLOAD PHASE
  ───────────────────────────────────────────────────────────── */
  return (
    <>
      <div className="cs-main" style={{ padding: 0, gap: 14, height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* Summary bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: 'var(--text-secondary)', flexShrink: 0, flexWrap: 'wrap' }}>
          <button
            onClick={onBackToOptions}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--accent-purple, #8b5cf6)', fontSize: 12, fontFamily: 'inherit',
              padding: 0, display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            ← Back
          </button>
          <span style={{ color: 'var(--border-glass)' }}>|</span>
          <span><strong style={{ color: 'var(--text-primary)' }}>Client:</strong> {createNewClient ? 'Create New' : selectedClientName}</span>
          <span style={{ color: 'var(--border-glass)' }}>|</span>
          <span><strong style={{ color: 'var(--text-primary)' }}>Policy:</strong> Create New</span>
        </div>

        {/* Document type selector */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '0 0 2px' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
            Document Type:
          </span>
          {(['Binder', 'Quote'] as const).map(dt => (
            <button
              key={dt}
              onClick={() => setDocumentType(dt)}
              style={{
                padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500,
                cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                background: documentType === dt ? 'var(--accent-purple, #8b5cf6)' : 'var(--surface-glass)',
                border: `1px solid ${documentType === dt ? 'var(--accent-purple, #8b5cf6)' : 'var(--border-glass)'}`,
                color: documentType === dt ? '#fff' : 'var(--text-secondary)',
              }}
            >
              {dt}
            </button>
          ))}
        </div>

        {/* Upload source tabs */}
        <div className="cs-tabs">
          <button
            className={`cs-tab${uploadTab === 'local' ? ' cs-tab--active' : ''}`}
            onClick={() => { setUploadTab('local'); setSelectedAmsFile(null); }}
          >
            Select Document from Local Drive
          </button>
          <button
            className={`cs-tab${uploadTab === 'client' ? ' cs-tab--active' : ''}`}
            onClick={() => { setUploadTab('client'); setFile(null); }}
          >
            Select Document from Client Profile
          </button>
        </div>

        {/* Tab content */}
        <div className="cs-content" style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {uploadTab === 'local' ? (
            <div
              className={`cs-dropzone${isDragging ? ' cs-dropzone--dragging' : ''}`}
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
            >
              {!file ? (
                <>
                  <div className="cs-dropzone__icon"><FiFileText size={28} /></div>
                  <p className="cs-dropzone__title">Drag & Drop your  PDF here</p>
                  <p className="cs-dropzone__or">or</p>
                  <motion.button
                    className="cs-browse-btn"
                    onClick={() => fileInputRef.current?.click()}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Browse Files
                  </motion.button>
                  <p className="cs-dropzone__types">Allowed: PDF only</p>
                </>
              ) : (
                <div className="cs-file-card">
                  <div className="cs-file-card__icon"><FiFileText size={20} /></div>
                  <div className="cs-file-card__info">
                    <span className="cs-file-card__name">{file.name}</span>
                    <span className="cs-file-card__size">{(file.size / 1024).toFixed(0)} KB</span>
                  </div>
                  <button className="cs-file-card__delete" onClick={() => setFile(null)} title="Remove">
                    <FiTrash2 size={15} />
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files) pickFile(e.target.files); e.target.value = ''; }}
              />
            </div>
          ) : (
            <CommissionScanClientFiles onFileSelected={setSelectedAmsFile} />
          )}
        </div>

        {/* Info banner */}
        <div className="cs-info-banner">
          <FiAlertCircle size={14} />
          <span>
            This tool works best with readable PDFs. Scanned or image-based PDFs may not process correctly.
            Consider converting to a readable format using OCR before uploading.
          </span>
        </div>

        {uploadError && (
          <div style={{ color: '#dc2626', fontSize: 12, padding: '4px 0' }}>{uploadError}</div>
        )}

        {/* Footer */}
        <div className="cs-footer">
          <span className="cs-footer__status">
            {uploadTab === 'local'
              ? (!file ? 'No file selected.' : `${file.name} ready to scan`)
              : (!selectedAmsFile ? 'No file selected.' : `${selectedAmsFile.file.name} ready to scan`)
            }
          </span>
          <div className="cs-footer__actions">
            <button
              className="cs-btn-cancel"
              disabled={uploadLoading}
              onClick={() => { setFile(null); setSelectedAmsFile(null); setUploadError(''); }}
            >
              Cancel
            </button>
            <motion.button
              className="cs-btn-start"
              disabled={!canScan}
              whileHover={{ scale: canScan ? 1.03 : 1 }}
              whileTap={{ scale: canScan ? 0.97 : 1 }}
              onClick={onStartScan}
            >
              {uploadLoading ? <span className="cs-upload-spinner" /> : '✦ Start Scan'}
            </motion.button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MTMScanUpload;

